import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, finalize } from 'rxjs/operators';
import { LocalStorageKeyEnum } from './localStorage.key.enum';
import { Error } from './models/error';
import { ModalErrorService } from './services/shared/modal-error.service';
import { LoadingService } from './services/shared/loading.service';
import { AuthService } from './services/auth/auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { showLoadingHelper } from './utils/showLoadingHelper';
import { Router } from '@angular/router';

@Injectable()
export class HTTPListener implements HttpInterceptor {
  constructor(
    private readonly modalService: ModalErrorService,
    private readonly loadingService: LoadingService,
    private readonly jwtHelper: JwtHelperService,
    public authService: AuthService,
    private readonly router: Router,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // ignoring loading state if loading dropdowns on sections
    const isShowLoading = showLoadingHelper(req);
    if (isShowLoading) this.loadingService.pushLoadingRequests(req);

    return next.handle(this.addAuthenticationToken(req)).pipe(
      catchError((error) => {
        if (isShowLoading) this.loadingService.popLoadingRequests();
        if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

        const errorTraceId = error.headers?.get('x-cloud-trace-context') ?? null;
        
        // 1. Gestione Errori Critici (500, 503)
        if (error.status === 500 || error.status === 503) {
          this.handleCriticalError(error, errorTraceId);
          return throwError(() => error.error);
        }

        // 2. Gestione Autenticazione (401)
        if (error.status === 401) {
          document.querySelector<HTMLElement>("#blanket")?.classList.add('d-none');
          this.router.navigate(["/login"]);
          return throwError(() => error.error);
        }

        // 3. Filtri Esclusione (Errori che non devono aprire Modali)
        if (this.shouldIgnoreError(error)) {
          return throwError(() => error.error);
        }

        // 4. Gestione Errori Validazione e altri messaggi
        this.handleValidationError(error, errorTraceId);
        
        return throwError(() => error.error);
      }),
      finalize(() => {
        if (isShowLoading) this.loadingService.popLoadingRequests();
      })
    );
  }

  // Gestisce i messaggi per errori server
  private handleCriticalError(error: HttpErrorResponse, errorTraceId: string | null) {
    const errorData = (error.error && error.message) ? error.error : {
      message: '500 - Internal Server Error',
      timestamp: error.error?.timestamp,
      errorTraceId
    };
    if (error.error && error.message) error.error.errorTraceId = errorTraceId;
    this.modalService.emitModalOpen(errorData as Error);
  }

  // Filtra gli URL che non devono scatenare logiche comuni
  private shouldIgnoreError(error: HttpErrorResponse): boolean {
    const url = error.url ?? '';
    if (url.includes('graph.microsoft.com') && error.status === 404) return true;
    if (url.includes('jira_user/activate') && error.status === 502) return true;
    if (url.includes('api/v1/order/budget/') && url.includes('/bulk')) return true;
    return false;
  }

  // Gestisce la logica complessa delle validazioni
  private handleValidationError(error: HttpErrorResponse, errorTraceId: string | null) {
    const vErrors = error.error?.validationErrors;
    const hasEmptyFieldName = vErrors?.some((er: any) => !er.fieldName);

    if (hasEmptyFieldName || (error.error && error.message && !error.url?.includes('user/jira/activate'))) {
      if (error.error) error.error.errorTraceId = errorTraceId;
      this.modalService.emitModalOpen(error.error as Error);
    }
  }


  addAuthenticationToken(request) {
    let accessToken = null;
    const user = localStorage.getItem(LocalStorageKeyEnum.USER);

    if (user) {
      const userObj = JSON.parse(user);
      const isExpiredToken = this.jwtHelper.isTokenExpired(userObj.idToken);
      if (!isExpiredToken) {
        accessToken = `Bearer ` + userObj.idToken;
      }
    }

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!accessToken) {
      return request;
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      setHeaders: {
        Authorization: accessToken,
      },
    });
  }
}
